<template>
  <SideSheet
    class="sidebar-filters"
    data-testid="sidebar-filters"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
      <template #center>{{ filterCount }} Filtros selecionados</template>
    </NavBar>
    <section class="sidebar-filters__content">
      <div
        v-for="(filter, index) in filtersData"
        v-show="filter.options.length > 0"
        :key="index"
        class="sidebar-filters__filter"
      >
        <Heading class="sidebar-filters__filter-title" :level="3">
          {{ filter.label }}
        </Heading>
        <FilterList
          :checked="filter.selectedFilters"
          class="sidebar-filters__filter-list"
          :options="filter.options"
          :multiple="filter.isMultiple"
          @change="value => handleChange(value, index, filter)"
        />
      </div>
    </section>
    <ButtonContainer class="sidebar-filters__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Filtrar"
          size="large"
          :disabled="filterCount === 0"
          @click="handleFilters"
        />
      </template>
      <template #button-secondary>
        <ButtonSecondary size="large" label="Limpar" @click="clearFilters" />
      </template>
    </ButtonContainer>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';

import {
  NavBar,
  Heading,
  FilterList,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';

interface IFilter {
  label: string;
  isMultiple: boolean;
  value: string;
  selectedFilters: string | string[];
  options: { label: string; value: string }[];
}

interface EmitEvents {
  (event: 'apply-filters', filters: IFilter[], count: number): void;
  (event: 'close'): void;
}
const emit = defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
  filters: IFilter[];
  clear?: boolean;
}
const props = withDefaults(defineProps<Props>(), { clear: false });

function handleChange(value: string, index: number, filter: IFilter) {
  if (filter.isMultiple) {
    filtersData.value[index].selectedFilters = value;
  } else if (filtersData.value[index].selectedFilters === value) {
    filtersData.value[index].selectedFilters = '';
  } else filtersData.value[index].selectedFilters = value;
}

watch(
  () => props.clear,
  clear => {
    if (clear) {
      clearFilters();
    }
  },
  { immediate: true }
);

const filtersData = ref<IFilter[]>([]);

const filterCount = computed(() => {
  let count = 0;

  filtersData.value.forEach(filter => {
    if (!filter.isMultiple && filter.selectedFilters) count += 1;
    else count += filter.selectedFilters.length;
  });

  return count;
});

function handleFilters() {
  const applyedFilters = filtersData.value.filter(
    filter => filter.selectedFilters.length > 0
  );

  emit('apply-filters', applyedFilters, filterCount.value);
}

function clearFilters() {
  filtersData.value = filtersData.value.map(filter => {
    return {
      ...filter,
      selectedFilters: filter.isMultiple ? [] : ''
    };
  });

  handleFilters();
}

onMounted(() => {
  filtersData.value = props.filters;
});
</script>

<style lang="less" scoped>
.sidebar-filters {
  :deep(.panel-content.right) {
    width: 698px;
  }

  :deep(.list) {
    flex-wrap: wrap;
  }

  :deep(.list li) {
    margin-left: 0;
    margin-right: @size-spacing-x300;
    margin-bottom: @size-spacing-x300;
  }

  &__content {
    padding: @size-spacing-x1000 @size-spacing-x1200 @size-spacing-x600;
  }

  &__filter {
    margin-bottom: @size-spacing-x800;
    border-bottom: @size-border-x400 @divider-primary solid;

    &:last-of-type {
      margin-bottom: 0;
      border-bottom: none;
    }

    &-title {
      margin-bottom: @size-spacing-x500;
    }

    &-list {
      margin-bottom: @size-spacing-x700;

      &:last-of-type {
        border-bottom: none;
      }
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 698px;
    position: sticky;
    bottom: 0;
  }
}
</style>
